import { Plus, X } from 'lucide-react'
import { useEffect } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { apiFetcher } from '@/data/fetcher'
import { Category } from '@/interfaces/catalog/Category'
import { Product } from '@/interfaces/catalog/Product'
import { flattenCategories, getRelatedCategoryIds } from '@/lib/catalog/categoryUtils'
import { cn } from '@/lib/utils'

import { ProductFormValues } from './schema'

interface ProductFormBasicLayoutProps {
  productId?: string
}

/** Hlavní sloupec: název, popis, typ produktu a položky balíčku. */
export const ProductFormBasicLayout = ({ productId }: ProductFormBasicLayoutProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.products' })
  const { control, watch, setValue, getValues } = useFormContext<ProductFormValues>()
  const productType = watch('productType')

  const { data: productsResponse } = useSWR<{ data?: Product[]; total?: number }>(
    productType === 'package' ? '/api/v2/products' : null,
    apiFetcher,
  )
  const allProducts = productsResponse?.data || []
  const availableProducts = productId ? allProducts.filter((p) => p.id !== productId) : allProducts

  const { fields, append, remove } = useFieldArray({ control, name: 'packageItems' })

  // Balíček má mít aspoň dvě položky
  useEffect(() => {
    if (productType === 'package') {
      const currentPackageItems = getValues('packageItems') || []
      for (let i = currentPackageItems.length; i < 2; i++) {
        append({ productId: '', quantity: 1 })
      }
    }
  }, [productType, getValues, append])

  return (
    <div className="grid gap-4">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.name.label')}</FormLabel>
            <FormControl>
              <Input placeholder={t('form.name.placeholder')} {...field} autoComplete="off" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.description.label')}</FormLabel>
            <FormControl>
              <Textarea
                rows={5}
                placeholder={t('form.description.placeholder')}
                {...field}
                value={field.value || ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="productType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.productType.label')}</FormLabel>
            <Select
              disabled={productId !== undefined}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                if (value !== 'package') setValue('packageItems', [])
              }}
            >
              <FormControl className="w-full sm:w-64">
                <SelectTrigger>
                  <SelectValue placeholder={t('form.productType.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {(['physical', 'virtual', 'service', 'package'] as const).map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`form.productType.options.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {productId !== undefined && (
              <p className="text-xs text-muted-foreground">Typ produktu po založení nejde změnit.</p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      {productType === 'package' && (
        <div className="grid gap-3 rounded-md bg-muted p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{t('form.packageItems.label')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => append({ productId: '', quantity: 1 })}>
              <Plus />
              {t('form.packageItems.add')}
            </Button>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('form.packageItems.empty')}</p>
          ) : (
            fields.map((item, index) => (
              <div key={item.id} className="flex items-end gap-2">
                <FormField
                  control={control}
                  name={`packageItems.${index}.productId`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-xs">{t('form.packageItems.product')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full bg-background">
                            <SelectValue placeholder={t('form.packageItems.productPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableProducts.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`packageItems.${index}.quantity`}
                  render={({ field }) => (
                    <FormItem className="w-24">
                      <FormLabel className="text-xs">{t('form.packageItems.quantity')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="1"
                          className="bg-background text-right tabular"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="button" variant="ghost" size="icon" aria-label="Odebrat položku" onClick={() => remove(index)}>
                  <X />
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/** Pravý sloupec: přepínač Aktivní. */
export const ProductFormStatusFields = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.products' })
  const { control } = useFormContext<ProductFormValues>()

  return (
    <FormField
      control={control}
      name="isActive"
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between gap-3 space-y-0">
          <FormLabel className="font-normal">{t('form.isActive.label')}</FormLabel>
          <FormControl>
            <Switch checked={field.value ?? true} onCheckedChange={field.onChange} />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

/** Pravý sloupec: hlavní a vedlejší kategorie. */
export const ProductFormCategoryFields = ({ categories = [] }: { categories?: Category[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'catalog.products' })
  const { control, watch, setValue, getValues } = useFormContext<ProductFormValues>()

  const categoryOptions = flattenCategories(categories)
  const mainCategoryId = watch('mainCategoryId')
  const secondaryCategoryIds = watch('secondaryCategoryIds') || []
  // Předci, sama kategorie a potomci hlavní kategorie nejdou zvolit jako vedlejší
  const relatedCategoryIds = mainCategoryId ? getRelatedCategoryIds(categories, mainCategoryId) : []

  useEffect(() => {
    if (mainCategoryId) {
      const relatedIds = getRelatedCategoryIds(categories, mainCategoryId)
      const currentSecondaryIds = getValues('secondaryCategoryIds') || []
      const filtered = currentSecondaryIds.filter((id) => !relatedIds.includes(id))
      if (filtered.length !== currentSecondaryIds.length) setValue('secondaryCategoryIds', filtered)
    }
  }, [mainCategoryId, categories, getValues, setValue])

  const toggleSecondary = (categoryId: string) => {
    setValue(
      'secondaryCategoryIds',
      secondaryCategoryIds.includes(categoryId)
        ? secondaryCategoryIds.filter((id) => id !== categoryId)
        : [...secondaryCategoryIds, categoryId],
    )
  }

  return (
    <div className="grid gap-4">
      <FormField
        control={control}
        name="mainCategoryId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.mainCategoryId.label')}</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl className="w-full">
                <SelectTrigger>
                  <SelectValue placeholder={t('form.mainCategoryId.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="secondaryCategoryIds"
        render={() => (
          <FormItem>
            <FormLabel>{t('form.secondaryCategoryIds.label')}</FormLabel>
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2">
              {categoryOptions.length === 0 ? (
                <p className="p-1 text-sm text-muted-foreground">{t('form.secondaryCategoryIds.empty')}</p>
              ) : (
                categoryOptions.map((option) => {
                  const isDisabled = relatedCategoryIds.includes(option.id)
                  return (
                    <label
                      key={option.id}
                      htmlFor={`secondary-${option.id}`}
                      className={cn(
                        'flex items-center gap-2 rounded-sm px-1 py-1 text-sm',
                        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted',
                      )}
                    >
                      <Checkbox
                        id={`secondary-${option.id}`}
                        checked={secondaryCategoryIds.includes(option.id)}
                        disabled={isDisabled}
                        onCheckedChange={() => toggleSecondary(option.id)}
                      />
                      <span className="truncate">{option.name}</span>
                    </label>
                  )
                })
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
